/**
 * THE CBD HUT — Full search supporting destinations
 *
 * Fetches Shopify Predictive Search resources for an already-submitted search
 * and presents a restrained set of matching brands, collections and guides.
 */
(() => {
  const ROOT_SELECTOR = '[data-cbh-search-destinations]';
  const CACHE = new Map();
  const CACHE_LIMIT = 20;
  const MAX_CARDS = 4;
  const CONTROLLERS = new WeakMap();

  class CbhSearchDestinations {
    constructor(root) {
      this.root = root;
      this.query = String(root.dataset.query || '').trim().replace(/\s+/g, ' ');
      this.sectionId = root.dataset.sectionId || 'cbh-search-supporting-destinations';
      this.endpoint = root.dataset.predictiveUrl || '/search/suggest';
      this.showGuides = root.dataset.showGuides !== 'false';
      this.grid = root.querySelector('[data-cbh-search-destinations-grid]');
      this.skeleton = root.querySelector('[data-cbh-search-destinations-skeleton]');
      this.status = root.querySelector('[data-cbh-search-destinations-status]');
      this.routesTemplate = root.querySelector('[data-cbh-search-destination-routes]');
      this.abortController = null;
    }

    normalise(value) {
      return String(value || '')
        .toLowerCase()
        .replace(/[’']/g, '')
        .replace(/&/g, ' and ')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim()
        .replace(/\s+/g, ' ');
    }

    normaliseIdentity(value) {
      return this.normalise(value)
        .replace(/\bthe\b/g, ' ')
        .replace(/\blimited\b/g, ' ')
        .replace(/\bltd\b/g, ' ')
        .trim()
        .replace(/\s+/g, ' ');
    }

    destinationQuery() {
      const query = this.normalise(this.query);
      const brandAliases = new Map([
        ['orang county', 'Orange County CBD'],
        ['orang county cbd', 'Orange County CBD'],
        ['orange country', 'Orange County CBD'],
        ['orange country cbd', 'Orange County CBD'],
        ['voyager cbd', 'Voyager'],
        ['reakiro cbd', 'Reakiro'],
      ]);

      return brandAliases.get(query) || this.query;
    }

    escapeHtml(value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }

    cacheKey() {
      const locale = document.documentElement.lang || 'en';
      return `${locale}|${this.normalise(this.query)}`;
    }

    getCached() {
      const key = this.cacheKey();
      return CACHE.has(key) ? CACHE.get(key) : '';
    }

    setCached(markup) {
      const key = this.cacheKey();
      if (CACHE.size >= CACHE_LIMIT && !CACHE.has(key)) {
        CACHE.delete(CACHE.keys().next().value);
      }
      CACHE.set(key, markup);
    }

    getCuratedRoutes() {
      if (!this.routesTemplate) return [];
      const seen = new Set();

      return Array.from(this.routesTemplate.content.querySelectorAll('a[href]'))
        .map((link) => ({
          href: link.getAttribute('href') || '',
          title: String(link.dataset.routeTitle || '').trim().replace(/\s+/g, ' '),
          collectionTitle: String(link.dataset.routeCollectionTitle || '').trim().replace(/\s+/g, ' '),
          image: String(link.dataset.routeImage || '').trim(),
        }))
        .filter((route) => {
          const key = `${route.href}|${this.normalise(route.collectionTitle || route.title)}`;
          if (!route.href || !route.title || seen.has(key)) return false;
          seen.add(key);
          return true;
        });
    }

    broadCollectionRoutes() {
      const query = this.normalise(this.query);
      const routes = this.getCuratedRoutes().filter((route) => route.collectionTitle);
      if (!routes.length) return [];

      const searchableTitle = (route) => this.normalise(`${route.title} ${route.collectionTitle || ''}`);
      const isGuidance = (title) => /\b(learn|guide|guides|faq|help|about|dosage)\b/.test(title);

      if (['cbd', 'cbd products', 'shop cbd'].includes(query)) {
        return routes
          .filter((route) => {
            const title = searchableTitle(route);
            return title.startsWith('cbd ') && !isGuidance(title);
          })
          .slice(0, 3);
      }

      if (['wellness', 'wellness products', 'supplements'].includes(query)) {
        return routes
          .filter((route) => {
            const title = searchableTitle(route);
            return !isGuidance(title) && /\b(wellness|mushroom|supplement|nootropic)\b/.test(title);
          })
          .slice(0, 3);
      }

      if (['vape', 'vapes', 'cbd vape', 'cbd vapes'].includes(query)) {
        return routes
          .filter((route) => {
            const title = searchableTitle(route);
            return !isGuidance(title) && /\b(vape|vapes|e liquid|eliquid)\b/.test(title);
          })
          .slice(0, 3);
      }

      return [];
    }

    routeCard(route) {
      const title = route.collectionTitle || route.title;
      const safeTitle = this.escapeHtml(title);
      const safeHref = this.escapeHtml(route.href);
      const safeImage = this.escapeHtml(route.image);
      const media = safeImage
        ? `<img src="${safeImage}" alt="" width="64" height="64" loading="eager" decoding="async">`
        : `<span class="cbh-search-destination-card__fallback">${this.escapeHtml(title.slice(0, 1).toUpperCase())}</span>`;

      const wrapper = document.createElement('div');
      wrapper.innerHTML = `
        <a
          class="cbh-search-destination-card cbh-search-destination-card--collection"
          href="${safeHref}"
          data-cbh-search-destination
          data-cbh-destination-kind="collection"
          data-cbh-collection-title="${safeTitle}"
          data-cbh-curated-route
        >
          <span class="cbh-search-destination-card__media" aria-hidden="true">${media}</span>
          <span class="cbh-search-destination-card__copy">
            <span class="cbh-search-destination-card__eyebrow">Shop collection</span>
            <span class="cbh-search-destination-card__title">${safeTitle}</span>
          </span>
          <span class="cbh-search-destination-card__arrow" aria-hidden="true">→</span>
        </a>`;
      return wrapper.firstElementChild;
    }

    brandCollectionAffinity(brandName, collectionName) {
      const brand = this.normaliseIdentity(brandName);
      const collection = this.normaliseIdentity(collectionName);
      if (!brand || !collection) return { score: 0, consolidate: false };
      if (collection === brand) return { score: 100, consolidate: true };
      if (!collection.startsWith(`${brand} `)) return { score: 0, consolidate: false };

      const suffix = collection.slice(brand.length).trim();
      const broadDestinations = new Set([
        'cbd',
        'cbd products',
        'cbd range',
        'products',
        'product range',
        'range',
        'collection',
        'mushroom supplements',
        'cbd topicals',
      ]);

      if (broadDestinations.has(suffix)) return { score: 90, consolidate: true };
      return { score: 60, consolidate: false };
    }

    enhanceBrands(brands, collections) {
      const claimed = new Set();

      brands.forEach((brand) => {
        const brandName = brand.dataset.cbhBrandName
          || brand.querySelector('.cbh-search-destination-card__title')?.textContent
          || '';

        const candidates = collections
          .filter((collection) => !claimed.has(collection) && !collection.hasAttribute('data-cbh-curated-route'))
          .map((collection) => ({
            collection,
            ...this.brandCollectionAffinity(
              brandName,
              collection.dataset.cbhCollectionTitle
                || collection.querySelector('.cbh-search-destination-card__title')?.textContent
                || ''
            ),
          }))
          .filter((candidate) => candidate.score > 0)
          .sort((a, b) => b.score - a.score);

        const best = candidates[0];
        if (!best) return;

        const sourceImage = best.collection.querySelector('.cbh-search-destination-card__media img');
        const brandMedia = brand.querySelector('.cbh-search-destination-card__media');
        if (sourceImage && brandMedia) {
          const logo = sourceImage.cloneNode(true);
          logo.alt = '';
          logo.loading = 'eager';
          brandMedia.replaceChildren(logo);
          brandMedia.classList.add('cbh-search-destination-card__media--image');
        }

        if (!best.consolidate) return;
        const destination = best.collection.getAttribute('href');
        if (destination) brand.setAttribute('href', destination);
        best.collection.remove();
        claimed.add(best.collection);
      });
    }

    dedupe(cards) {
      const seenUrls = new Set();
      const seenTitles = new Set();

      return cards.filter((card) => {
        const href = card.getAttribute('href') || '';
        const title = this.normalise(card.querySelector('.cbh-search-destination-card__title')?.textContent || '');
        if (!href || !title || seenUrls.has(href) || seenTitles.has(title)) return false;
        seenUrls.add(href);
        seenTitles.add(title);
        return true;
      });
    }

    isRelevantGuide(card) {
      const title = this.normalise(card.querySelector('.cbh-search-destination-card__title')?.textContent || '');
      const query = this.normalise(this.query);
      if (!title || !query) return false;

      const utilityPagePattern = /\b(contact|privacy|terms|returns|refund|shipping|delivery|account|login|register|cookies|policy|policies)\b/;
      if (utilityPagePattern.test(title)) return false;
      if (title.includes(query)) return true;

      const ignoredWords = new Set(['and', 'for', 'from', 'shop', 'product', 'products', 'search', 'the', 'with']);
      const tokens = query
        .split(' ')
        .filter((token) => token.length >= 3 && !ignoredWords.has(token));

      return tokens.length > 0 && tokens.every((token) => title.includes(token));
    }

    selectCards(response) {
      const broadRoutes = this.broadCollectionRoutes();
      let brands = Array.from(response.querySelectorAll('[data-cbh-destination-kind="brand"]'));
      let collections = Array.from(response.querySelectorAll('[data-cbh-destination-kind="collection"]'));
      let guides = Array.from(response.querySelectorAll('[data-cbh-destination-kind="guide"]'));

      if (broadRoutes.length >= 2) {
        collections = broadRoutes.map((route) => this.routeCard(route));
      }

      this.enhanceBrands(brands, collections);
      brands = this.dedupe(brands.filter((card) => card.isConnected)).slice(0, 2);
      collections = this.dedupe(collections.filter((card) => card.isConnected || card.hasAttribute('data-cbh-curated-route')));
      guides = this.showGuides ? this.dedupe(guides.filter((card) => this.isRelevantGuide(card))) : [];

      const chosen = [];
      chosen.push(...brands.slice(0, 2));

      const reserveForGuide = guides.length > 0 && chosen.length < MAX_CARDS ? 1 : 0;
      const firstCollectionLimit = Math.max(0, Math.min(3, MAX_CARDS - chosen.length - reserveForGuide));
      chosen.push(...collections.slice(0, firstCollectionLimit));

      if (guides.length && chosen.length < MAX_CARDS) chosen.push(guides[0]);

      if (chosen.length < MAX_CARDS) {
        const alreadyChosen = new Set(chosen);
        collections.forEach((collection) => {
          if (chosen.length >= MAX_CARDS || alreadyChosen.has(collection)) return;
          chosen.push(collection);
          alreadyChosen.add(collection);
        });
      }

      return this.dedupe(chosen).slice(0, MAX_CARDS);
    }

    render(markup) {
      const parsed = new DOMParser().parseFromString(markup, 'text/html');
      const response = parsed.querySelector('[data-cbh-search-destinations-response]');
      if (!response) {
        this.hide();
        return;
      }

      const cards = this.selectCards(response);
      if (!cards.length) {
        this.hide();
        return;
      }

      this.grid.replaceChildren(...cards.map((card) => card.cloneNode(true)));
      this.skeleton.hidden = true;
      this.grid.hidden = false;
      this.root.removeAttribute('aria-busy');
      this.root.classList.add('is-ready');
      if (this.status) {
        this.status.textContent = `${cards.length} related destination${cards.length === 1 ? '' : 's'} available.`;
      }
    }

    hide() {
      this.root.hidden = true;
      this.root.removeAttribute('aria-busy');
    }

    destroy() {
      if (this.abortController) {
        try {
          this.abortController.abort();
        } catch (error) {
          // No action needed.
        }
      }
      this.abortController = null;
    }

    async init() {
      if (!this.query || this.query.length < 2 || !this.grid || !this.skeleton) {
        this.hide();
        return;
      }

      this.root.hidden = false;

      const cached = this.getCached();
      if (cached) {
        this.render(cached);
        return;
      }

      const url = new URL(this.endpoint, window.location.origin);
      url.searchParams.set('q', this.destinationQuery());
      url.searchParams.set('section_id', this.sectionId);
      url.searchParams.set('resources[type]', 'collection,page,article');
      url.searchParams.set('resources[limit]', '5');
      url.searchParams.set('resources[limit_scope]', 'each');

      this.abortController = new AbortController();

      try {
        const response = await fetch(url.toString(), {
          signal: this.abortController.signal,
          headers: { Accept: 'text/html' },
        });
        if (!response.ok) throw new Error(`Search destinations failed: ${response.status}`);

        const text = await response.text();
        const parsed = new DOMParser().parseFromString(text, 'text/html');
        const section = parsed.querySelector(`#shopify-section-${this.sectionId}`);
        const markup = section?.innerHTML?.trim() || '';
        this.setCached(markup);
        this.render(markup);
      } catch (error) {
        if (error?.name !== 'AbortError') this.hide();
      }
    }
  }

  const initialise = (scope = document) => {
    scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (root.dataset.cbhDestinationsInitialised === 'true') return;
      root.dataset.cbhDestinationsInitialised = 'true';
      const controller = new CbhSearchDestinations(root);
      CONTROLLERS.set(root, controller);
      controller.init();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialise());
  } else {
    initialise();
  }

  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const roots = [];
    if (event.target?.matches?.(ROOT_SELECTOR)) roots.push(event.target);
    event.target?.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => roots.push(root));

    roots.forEach((root) => {
      CONTROLLERS.get(root)?.destroy();
      CONTROLLERS.delete(root);
    });
  });
})();
